// CoverImage — editorial cover for Blog / Explorer / Atlas cards.
//
// Strategy per `kind`:
//  • appellation → live mini iframe of the geoVINUM interactive map so
//    the visitor sees the actual vignoble outline, not a random
//    Wikipedia photo. Token is fetched once and shared across every
//    card of the page (module-cached).
//  • cepage      → Wikipedia lookup first; if it returns nothing, we
//    fall back to a **branded grape picto** (SVG) — no more mismatched
//    photos when Wikipedia hasn't indexed the grape.
//  • producer / everything else → same Wikipedia-with-picto-fallback.
//
// Every mode is IntersectionObserver-gated so off-screen cards cost 0
// network requests. Aspect ratio is driven by `aspectClass` so the
// same component fits both magazine and tight index tiles.
import { useEffect, useRef, useState } from "react";
import { Grape, MapPin } from "lucide-react";
import { apiFetch } from "@/lib/api";

// ─── Wikipedia lookup cache ─────────────────────────────────────────
const _wikiCache = new Map();       // key = `${kind}:${q.toLowerCase()}`
const _wikiInflight = new Map();

async function fetchWikiCover(query, kind) {
  const key = `${kind}:${query.toLowerCase()}`;
  if (_wikiCache.has(key)) return _wikiCache.get(key);
  if (_wikiInflight.has(key)) return _wikiInflight.get(key);
  const p = (async () => {
    const { ok, data } = await apiFetch(
      `/api/public/cover-image?q=${encodeURIComponent(query)}&kind=${encodeURIComponent(kind)}`
    );
    const payload = ok && data?.found ? { url: data.url, thumb_url: data.thumb_url } : null;
    _wikiCache.set(key, payload);
    _wikiInflight.delete(key);
    return payload;
  })();
  _wikiInflight.set(key, p);
  return p;
}

// ─── geoVINUM iframe token — one fetch shared by every appellation card ─
let _tokenPromise = null;
function getMapToken() {
  if (_tokenPromise) return _tokenPromise;
  _tokenPromise = (async () => {
    const { ok, data } = await apiFetch(`/api/geovinum-token`);
    return ok && data?.token ? data.token : null;
  })();
  return _tokenPromise;
}
const IFRAME_BASE = "https://iframe.geovinum.app/";

// ─── Branded pictos — used when no photo is available ───────────────
const CepagePicto = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[color:var(--gv-mist)] via-[color:var(--gv-mist)] to-[color:var(--gv-pink)]/15">
    <Grape size={54} strokeWidth={1.4} className="text-[color:var(--gv-pink)]/70" aria-hidden />
  </div>
);
const AppellationPicto = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[color:var(--gv-mist)] via-[color:var(--gv-mist)] to-[color:var(--gv-blue)]/15">
    <MapPin size={54} strokeWidth={1.4} className="text-[color:var(--gv-blue)]/70" aria-hidden />
  </div>
);
const pictoFor = (kind) => {
  if (kind === "cepage") return <CepagePicto />;
  if (kind === "appellation") return <AppellationPicto />;
  return <AppellationPicto />;
};

const CoverImage = ({ query, kind = "appellation", alt = "", aspectClass = "aspect-[4/3]", className = "", testId }) => {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible || !wrapRef.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { setVisible(true); io.disconnect(); }
      });
    }, { rootMargin: "200px" });
    io.observe(wrapRef.current);
    return () => io.disconnect();
  }, [visible]);

  // ─── APPELLATION → live mini iframe map ─────────────────────────
  if (kind === "appellation") {
    return (
      <AppellationCover
        wrapRef={wrapRef}
        visible={visible}
        query={query}
        alt={alt}
        aspectClass={aspectClass}
        className={className}
        testId={testId}
      />
    );
  }

  // ─── CEPAGE / PRODUCER / OTHER → Wiki photo with picto fallback ─
  return (
    <PhotoCover
      wrapRef={wrapRef}
      visible={visible}
      query={query}
      kind={kind}
      alt={alt}
      aspectClass={aspectClass}
      className={className}
      testId={testId}
    />
  );
};

const PhotoCover = ({ wrapRef, visible, query, kind, alt, aspectClass, className, testId }) => {
  // `cover` is a tri-state: undefined = not fetched yet, null = fetched
  // but no photo (use picto), object = photo url ready to render.
  const [cover, setCover] = useState(() => {
    const cached = _wikiCache.get(`${kind}:${(query || "").toLowerCase()}`);
    return cached === undefined ? undefined : cached;
  });
  useEffect(() => {
    if (!visible || cover !== undefined || !query) return;
    let cancelled = false;
    fetchWikiCover(query, kind).then((r) => { if (!cancelled) setCover(r); });
    return () => { cancelled = true; };
  }, [visible, query, kind, cover]);
  return (
    <div
      ref={wrapRef}
      className={`${aspectClass} relative overflow-hidden bg-[color:var(--gv-mist)] ${className}`}
      data-testid={testId}
    >
      {cover === undefined && visible && pictoFor(kind)}
      {cover === null && pictoFor(kind)}
      {cover && cover.url && (
        <img
          src={cover.thumb_url || cover.url}
          alt={alt || query}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
          onError={() => setCover(null)}
        />
      )}
    </div>
  );
};

const AppellationCover = ({ wrapRef, visible, query, alt, aspectClass, className, testId }) => {
  const [token, setToken] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!visible || token || failed) return;
    let cancelled = false;
    const safety = setTimeout(() => { if (!cancelled) setFailed(true); }, 8000);
    getMapToken().then((t) => {
      if (cancelled) return;
      if (t) setToken(t);
      else setFailed(true);
    });
    return () => { cancelled = true; clearTimeout(safety); };
  }, [visible, token, failed]);

  const src = token
    ? `${IFRAME_BASE}?token=${encodeURIComponent(token)}&app=${encodeURIComponent(query || "")}`
    : "";

  return (
    <div
      ref={wrapRef}
      className={`${aspectClass} relative overflow-hidden bg-[color:var(--gv-mist)] ${className}`}
      data-testid={testId}
      aria-label={alt || query}
    >
      {token && !failed ? (
        <iframe
          src={src}
          title={`Carte ${query}`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="absolute inset-0 w-full h-full"
          style={{ border: 0, pointerEvents: "none" }}
          onError={() => setFailed(true)}
          data-testid={testId ? `${testId}-iframe` : undefined}
        />
      ) : (
        pictoFor("appellation")
      )}
    </div>
  );
};

export default CoverImage;
